/**
 * Decides whether a visit lands on the marketing page or straight in the editor.
 *
 * Kept separate from main.js so it can be tested: main.js boots the app and
 * registers a service worker as a side effect of being imported.
 *
 * The editor itself needs no account — editing, recording, local import/export
 * and IndexedDB persistence are all plain browser APIs. Only the four cloud
 * menu items need Puter, and those ask for sign-in when they're used.
 */

/** localStorage key recording that someone has opened the editor before. */
export const LAUNCH_FLAG = 'ae:launched';

/**
 * @param {object} state
 * @param {boolean} state.hasPuter    Puter SDK present on the page
 * @param {string} [state.env]        puter.env — 'app' inside the Puter shell
 * @param {boolean} [state.signedIn]  puter.auth.isSignedIn()
 * @param {boolean} [state.hasLaunched] LAUNCH_FLAG is set
 * @param {boolean} [state.forceHome] ?home was passed, to get back to the pitch
 * @param {boolean} [state.forceEditor] ?editor was passed from a documentation link
 * @returns {boolean} true to show the landing page
 */
export function shouldShowLanding({
  hasPuter,
  env,
  signedIn = false,
  hasLaunched = false,
  forceHome = false,
  forceEditor = false
}) {
  // An explicit request for the marketing page always wins — it is the only way
  // back once the launch flag is set.
  if (forceHome) return true;
  if (forceEditor) return false;

  // No SDK (blocked, offline, self-hosted): there is no landing CTA that could
  // work, so sending someone to a page whose only button is broken is worse
  // than just giving them the editor.
  if (!hasPuter) return false;

  // Inside the Puter shell auth is implicit and the landing never applies.
  if (env === 'app') return false;

  if (signedIn) return false;

  // Returning visitors go straight back to their work. Without this, a
  // signed-out user's auto-saved session sits restored but hidden behind a
  // marketing page on every single visit.
  return !hasLaunched;
}

/** Read the launch flag, tolerating storage being unavailable (private mode). */
export function hasLaunchedBefore(storage) {
  try {
    return (storage || localStorage).getItem(LAUNCH_FLAG) === '1';
  } catch (e) {
    return false;
  }
}

/** Record that the editor has been opened. Never throws. */
export function rememberLaunch(storage) {
  try {
    (storage || localStorage).setItem(LAUNCH_FLAG, '1');
  } catch (e) {
    // Private browsing or storage disabled — the visitor just sees the landing
    // page again next time, which is a nuisance rather than a failure.
  }
}

/** Clear the launch flag so the landing page shows again. Never throws. */
export function forgetLaunch(storage) {
  try {
    (storage || localStorage).removeItem(LAUNCH_FLAG);
  } catch (e) {
    /* nothing to clear */
  }
}
